import React, { useState, useRef, useEffect } from "react";
import JobCard from "../JobsSection/JobCard";

function JobSearchResultsAI({
  jobs,
  isLoading,
  error,
  onSelectJob,
  onShowDetailsModal,
  selectedJobId,
  canLoadMore,
  isLoadingMore,
  onLoadMore,
}) {
  const displayedJobs = jobs || [];

  // --- NEW: State to control the visibility of the "Load More" button ---
  const [showLoadMore, setShowLoadMore] = useState(false);

  // --- NEW: A ref for the invisible trigger element at the bottom of the list ---
  const loadMoreTriggerRef = useRef(null);

  // --- NEW: useEffect to set up the IntersectionObserver ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // When the trigger element is intersecting (visible), show the button.
        // We only want to show it, not hide it again on scroll up.
        if (entry.isIntersecting) {
          setShowLoadMore(true);
        }
      },
      {
        root: null, // observes intersections relative to the viewport
        rootMargin: "0px",
        threshold: 1.0, // trigger when 100% of the element is visible
      }
    );

    const currentTrigger = loadMoreTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    // Cleanup function to disconnect the observer when the component unmounts
    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, []); // Run this effect only once on mount

  // --- NEW: Another effect to reset button visibility when new jobs are loaded ---
  // When isLoadingMore becomes true, it means we've clicked the button.
  // We hide the button again until the user scrolls to the new bottom.
  useEffect(() => {
    if (isLoadingMore) {
      setShowLoadMore(false);
    }
  }, [isLoadingMore]);

  const renderJobCard = (job, index) => (
    <JobCard
      key={job.id || job.job_link || `ai-job-${index}`}
      job={job}
      onSelectCard={onSelectJob}
      onShowDetailsModal={onShowDetailsModal}
    />
  );

  return (
    <div className="ai-jobs-results">
      {/* Loading, Error, and No Results states are unchanged */}
      {isLoading && <div className="smai-results-loading">...</div>}
      {!isLoading && error && (
        <div className="smai-error-message smai-results-error">...</div>
      )}
      {!isLoading && !error && displayedJobs.length === 0 && (
        <div className="smai-no-results">...</div>
      )}

      {/* Job Grid */}
      {!isLoading && !error && displayedJobs.length > 0 && (
        <div className="smai-jobs-grid">{displayedJobs.map(renderJobCard)}</div>
      )}

      {/* --- NEW: The invisible trigger element and the conditional button --- */}
      {/* This section only renders if there are more pages to load */}
      {canLoadMore && (
        <>
          {/* This is the container for our new button */}
          <div className="smai-load-more-container">
            {/* Show button ONLY when the trigger has been seen */}
            {showLoadMore && (
              <button
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="smai-load-more-button"
              >
                {isLoadingMore ? "Loading..." : "Load More Jobs"}
              </button>
            )}
          </div>

          {/* This is the invisible element we watch. It's placed after the button container. */}
          {/* We give it a ref and a small height so the observer can see it. */}
          <div
            ref={loadMoreTriggerRef}
            style={{ height: "1px", marginTop: "-1px" }}
          />
        </>
      )}
    </div>
  );
}

export default JobSearchResultsAI;
